// src/components/charts/MetricLineChart.jsx
import React, { memo, useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LinearScale,
  CategoryScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { getPrimaryColour } from "../../utils/getPrimaryColour";
import LoadingBouncyDots from "../ui/LoadingBouncyDots";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title
);

/**
 * MetricLineChart
 * - If `datasets` is omitted or empty and preset="none", renders an empty chart.
 * - If preset="decay" and datasets not provided, renders a synthetic decay curve (opt-in).
 * - When `loading` is true, shows a non-blocking bouncy-dots overlay on top of the chart.
 */
function MetricLineChart({
  height = 240,

  // Data API
  datasets,
  xType = "linear",
  xLabel,
  yLabel,

  // Axis ranges (optional)
  xMin,
  xMax,
  yMin,
  yMax,

  // Formatters (optional)
  xTickFormat,
  yTickFormat,
  xTickStep,

  // UI
  title,
  showLegend = false,
  showTooltip = false,
  showGrid = true,
  showYTickLabels = true, // <— NEW: hide Y-axis tick labels but keep grid/border

  // Loading overlay
  loading = false,
  loadingText = "",
  overlayClassName = "bg-white/40 backdrop-blur-[1px]",

  connectPoints = true,

  // Preset behaviour
  preset = "none", // "none" | "decay"
  duration = 300,
  step = 4,
  a = 5,
  k = 0.06,
  c = 2.5,
  defaultTension = 0.25,
  defaultBorderWidth = 3,
  defaultPointRadius = 0,
}) {
  const primary = getPrimaryColour();

  const decayData = useMemo(() => {
    if (datasets && datasets.length) return null;
    if (preset !== "decay") return null;

    const decay = (t) => a * Math.exp(-k * t) + c;
    const pts = [];
    for (let t = 0; t <= duration; t += step) pts.push({ x: t, y: decay(t) });

    const ys = pts.map((p) => p.y);
    const ymin = Math.min(...ys);
    const ymax = Math.max(...ys);
    return {
      datasets: [
        {
          label: "Decay Fit",
          data: pts,
          borderColor: primary,
          borderWidth: defaultBorderWidth,
          tension: defaultTension,
          pointRadius: defaultPointRadius,
        },
      ],
      ypad: (ymax - ymin || 1) * 0.1,
      xMin: 0,
      xMax: duration,
    };
  }, [
    datasets,
    preset,
    a,
    k,
    c,
    duration,
    step,
    defaultBorderWidth,
    defaultPointRadius,
    defaultTension,
    primary,
  ]);

  const finalDatasets = useMemo(() => {
    if (datasets && datasets.length) {
      return datasets.map((ds) => ({
        borderColor: ds.borderColor ?? primary,
        borderWidth: ds.borderWidth ?? defaultBorderWidth,
        pointRadius: ds.pointRadius ?? defaultPointRadius,
        tension: ds.tension ?? defaultTension,
        fill: false,
        // allow per-dataset override via ds.showLine; otherwise follow component-level prop
        showLine:
          typeof ds.showLine === "boolean" ? ds.showLine : connectPoints,
        // sensible defaults for clickable markers
        pointHoverRadius:
          ds.pointHoverRadius ??
          Math.max(4, ds.pointRadius ?? defaultPointRadius),
        pointHitRadius: ds.pointHitRadius ?? 6,
        ...ds,
      }));
    }
    return decayData?.datasets ?? [];
  }, [
    datasets,
    decayData,
    primary,
    defaultBorderWidth,
    defaultPointRadius,
    defaultTension,
    connectPoints,
  ]);

  const hasData = finalDatasets.some((ds) => (ds.data ?? []).length > 0);

  const computedRanges = useMemo(() => {
    if (!hasData) return { minY: 0, maxY: 1 };
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    finalDatasets.forEach((ds) => {
      (ds.data ?? []).forEach((p) => {
        if (typeof p?.y === "number") {
          minY = Math.min(minY, p.y);
          maxY = Math.max(maxY, p.y);
        }
      });
    });
    if (minY === Number.POSITIVE_INFINITY) return { minY: 0, maxY: 1 };
    return { minY, maxY };
  }, [finalDatasets, hasData]);

  const autoYPad = decayData?.ypad ?? 0;

  const toMmSs = (secs) => {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60)
      .toString()
      .padStart(2, "0");
    return `${m}:${s}`;
  };

  const data = { datasets: finalDatasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: showLegend },
      tooltip: { enabled: showTooltip },
      title: title ? { display: true, text: title } : { display: false },
    },
    scales: {
      x: {
        type: xType,
        min: xMin ?? decayData?.xMin ?? 0,
        max: xMax ?? decayData?.xMax ?? 1,
        grid: { color: showGrid ? "rgba(0,0,0,0.06)" : "transparent" },
        border: { color: "rgba(0,0,0,0.2)" },
        ticks: {
          display: true,
          callback: (v) => (xTickFormat ? xTickFormat(v) : v),
          maxTicksLimit: 7,
          stepSize:
            typeof xTickStep === "number"
              ? xTickStep
              : preset === "decay" && xType === "linear"
              ? 60
              : undefined,
        },
        title: {
          display: !!xLabel,
          text: xLabel ?? (preset === "decay" ? "Time" : undefined),
        },
      },
      y: (() => {
        const y = {
          grid: { color: showGrid ? "rgba(0,0,0,0.06)" : "transparent" },
          border: { color: "rgba(0,0,0,0.2)" },
          ticks: {
            display: showYTickLabels, // <— NEW: hide/show tick labels only
            callback: (v) => (yTickFormat ? yTickFormat(v) : v),
          },
          title: { display: !!yLabel, text: yLabel },
        };
        if (typeof yMin === "number") y.min = yMin;
        if (typeof yMax === "number") y.max = yMax;
        if (
          typeof yMin !== "number" &&
          typeof yMax !== "number" &&
          preset === "decay"
        ) {
          y.min = computedRanges.minY - autoYPad;
          y.max = computedRanges.maxY + autoYPad;
        }
        return y;
      })(),
    },
    animation: { duration: 600 },
  };

  if (!xTickFormat && preset === "decay" && xType === "linear") {
    options.scales.x.ticks.callback = (v) => toMmSs(v);
  }

  return (
    <div className="relative" style={{ height }}>
      <Line data={data} options={options} />
      {(loading || !hasData) && (
        <div
          className={`pointer-events-none absolute inset-0 flex items-center justify-center `}
          aria-hidden="true"
        >
          <LoadingBouncyDots text={loadingText} />
        </div>
      )}
    </div>
  );
}

export default memo(MetricLineChart);
